import React from 'react';
import { DetailCardType } from 'types/DetailPageTypes';
import ContactForm from '../../views/contacts/ContactForm';
import PersonForm from '../../views/contacts/PersonForm';
import LocationForm from '../../views/contacts/LocationForm';
import { getContactNumberSuffix } from '../../views/contacts/contactTypeOptions';

// Function to determine the client field link url based on its entity type
const determineLinkUrl = (recipient: any) => {
    if (recipient && recipient['entity_type'] && recipient['id']) {
        const baseUrl = recipient['entity_type'] === 'organization' ? '/contacts/organization' : '/contacts/person';
        return `${baseUrl}/${recipient['id']}`;
    }
    return null;
}

const filterLocationsOnRecipient = (recipient: any) => {
    if (recipient) {

        const contactFilterParam = { organization_id: recipient['id'] };

        // console.log(contactFilterParam)

        return contactFilterParam;
    }
    return null;
}

// Builds the field sections of an invoice, shared by the invoice modal and the invoice template modal
export const buildInvoiceFieldSections = ({
    itemId,
    environmentSettings,
    includeDates,
}: {
    itemId?: number;
    environmentSettings: Record<string, any> | null;
    includeDates: boolean;
}): DetailCardType[] => {

    // Define the base fields of the document
    const baseFields: DetailCardType[] = [
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'invoice.general.recipient_header',
                    fields: [
                        { 
                            type: 'searchselect', 
                            name: 'recipient',
                            label: 'invoice.general.recipient_label',
                            placeholder: 'invoice.general.recipient_placeholder',
                            objectName: 'contact.general.object_name.singular',
                            query: {
                                // Get all contacts which have the contact type 'contact', 'lead' and 'client'
                                endpoint: 'get_contact_search',
                                params: {
                                    contact_type: ['contact', 'lead', 'client'] 
                                }
                            },
                            viewModeLinkUrl: { function: determineLinkUrl },
                            viewModeSuffix: getContactNumberSuffix,
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                                subtitle: { field: 'primary_location', format: (value) => value.address, optional: true},
                            },
                            addNewItem: { method: 'modal-form', modalForm: <ContactForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'recipient_person',
                            label: 'invoice.general.recipient_person_label',
                            placeholder: 'invoice.general.recipient_person_placeholder',
                            objectName: 'person.general.object_name.singular',
                            query: {
                                // Only get persons and when a recipient is selected, get the persons connected to that contact
                                endpoint: 'get_person_search',
                                params: {
                                    
                                }
                            },
                            dataCondition: [{'recipient.entity_type': 'organization'}],
                            viewModeLinkUrl: { baseUrl: '/contacts/person' },
                            selectionFormat: 'name',
                            optionFormat: {
                                title: { field: 'name', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <PersonForm /> }
                        },
                        { 
                            type: 'searchselect', 
                            name: 'invoice_address',
                            label: 'invoice.general.invoice_address_label',
                            placeholder: 'invoice.general.invoice_address_placeholder',
                            objectName: 'location.general.object_name.singular',
                            query: {
                                // Get all active locations
                                // TODO: Get all locations and when a client is selected, get the primary locations connected to that contact
                                endpoint: 'get_location_search',
                                params: filterLocationsOnRecipient
                            },
                            postEndpoint: 'post_location',
                            selectionFormat: 'address',
                            optionFormat: {
                                title: { field: 'address', format: (value) => value },
                            },
                            addNewItem: { method: 'modal-form', modalForm: <LocationForm /> }
                        },
                    ],
                },
                {
                    fields: [
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            alignment: 'horizontal',
            fieldColumns: [
                {
                    title: 'invoice.general.details_header',
                    fields: [
                        { 
                            type: 'dropdown', 
                            name: 'workflow', 
                            label: 'invoice.general.type_label', 
                            apiRequest: [{
                                endpoint: 'get_workflow_list',
                                object: 'workflow',
                                params: { is_active: true, connected_feature: 'invoice' },
                                useCache: true
                            }],
                            selectionFormat: 'name',
                            optionFormat: 'name',
                            orderOptions: true,
                            backendField: 'id',
                            disabled_selected: 'invoice.general.type_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        { 
                            type: 'reference', 
                            name: 'reference',
                            label: 'invoice.general.reference_label',
                        },
                        {   // Only shown when the invoice was created from a project
                            type: 'linked-project',
                            name: 'project',
                            label: 'invoice.general.project_label',
                            dataCondition: 'project',
                        },
                        { 
                            type: 'dropdown', 
                            name: 'division', 
                            label: 'job.general.division_label', 
                            apiRequest: [{
                                endpoint: 'get_division_list',
                                object: 'division',
                                params: { is_active: true },
                                useCache: true
                            }],
                            activeFeature: 'divisions',
                            disabled_selected: 'job.general.division_disabled_selected',
                            allowNoneOption: false,
                            showSearch: false,
                        },
                        // { 
                        //     type: 'multiselect', 
                        //     name: 'labels', 
                        //     label: 'invoice.general.labels_label', 
                        //     apiObject: 'label',
                        //     apiRequest: [{
                        //         endpoint: 'get_label_list',
                        //         object: 'label',
                        //         params: { is_active: true, connected_feature: 'invoices' }
                        //     }],
                        //     allowNoneOption: false,
                        //     showSearch: false,
                        //     disabled_selected: 'invoice.general.labels_disabled_selected',
                        // },
                    ],
                },
                {
                    fields: [
                        ...(includeDates ? [{
                            type: 'sent-date' as const,
                            name: 'invoice_date',
                            label: 'invoice.general.invoice_date_label',
                            tooltipText: 'invoice.general.invoice_date_tooltipText',
                        }] : []),
                        {
                            type: 'expiration-days',
                            name: 'expiration_days',
                            label: 'invoice.general.due_date_label',
                            featureName: 'invoice'
                        }
                    ]
                }
            ]
        },
        {
            component: 'FieldsCard',
            title: 'invoice.general.lines_header',
            fields: [
                { 
                    name: 'lines',
                    type: 'document-lines',
                    featureName: 'invoice',
                    documentId: itemId,
                    apiRequest: [
                        {
                            endpoint: 'get_vatrate_list',
                            object: 'vatrate',
                            params: { is_active: true }
                        },
                        {
                            endpoint: 'get_ledgeraccount_list',
                            object: 'ledgeraccount',
                            params: { is_active: true, ledger_account_type: 'revenue' }
                        },
                        {
                            endpoint: 'get_currency_list',
                            object: 'currency',
                            params: {}
                        }
                    ]
                }
            ]
        },
    ]

    // Define the optional tax blocked account field
    const taxBlockedAccountField: DetailCardType | null =
        environmentSettings?.invoice_show_tax_blocked_acount ? {
            component: 'FieldsCard',
            title: 'invoice.general.tax_blocked_account_text_label',
            alignment: 'stacked',
            fields: [
                { 
                    type: 'text', 
                    name: 'tax_blocked_account_text', 
                    emptyLabel: 'invoice.general.tax_blocked_account_text_emptyLabel',
                    placeholder: 'invoice.general.tax_blocked_account_text_placeholder', 
                },
            ]
        } : null

    // Define the terms and conditions field
    const termsAndConditionsField: DetailCardType = {
        component: 'FieldsCard',
        title: 'invoice.general.terms_and_conditions_header',
        alignment: 'stacked',
        fields: [
            {
                type: 'condition-lines',
                name: 'condition_lines',
            },
            { 
                type: 'textarea', 
                name: 'terms_and_conditions', 
                emptyLabel: 'invoice.general.terms_and_conditions_emptyLabel',
                placeholder: 'invoice.general.terms_and_conditions_placeholder', 
                enableStyling: 'simple',
                showToolbarInitially: true,
                rows: 3,
            },
        ]
    }

    // Combine the sections
    const sections: DetailCardType[] = [
        ...baseFields,
        ...(taxBlockedAccountField ? [taxBlockedAccountField] : []),
        termsAndConditionsField,
    ];

    return sections;
};
